import { CircleNotchIcon } from "@phosphor-icons/react";
import { useI18N } from "common/hooks";

import VenuePlaceholderImage from "@/components/common/VenuePlaceholderImage";
import { formatPlaces } from "../utils/formatPlaces";

interface OnboardingCityCardProps {
  name: string;
  country: string;
  /** Venues with accessibility information. */
  count: number;
  /**
   * Wikipedia thumbnail. `undefined` means the batch request is still in
   * flight; `null` means the city has no usable image.
   */
  photoUrl?: string | null;
  /** True while the click is being resolved to coordinates. */
  isSelecting?: boolean;
  onSelect: () => void;
}

/**
 * Browse-grid card for a city we hold accessibility data for — square photo,
 * then country, city and venue count (Figma "Splash card").
 *
 * No height is fixed anywhere: the photo is a 1:1 ratio of whatever width the
 * column gives it, and the text below simply grows when the user scales their
 * font up.
 */
const OnboardingCityCard = ({
  name,
  country,
  count,
  photoUrl,
  isSelecting = false,
  onSelect,
}: OnboardingCityCardProps) => {
  const i18n = useI18N();
  const isLoadingPhoto = photoUrl === undefined;

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={isSelecting}
      aria-busy={isSelecting}
      // The visible text is three separate lines; read together they are the
      // answer, so the whole sentence is the button's name.
      aria-label={`${name}, ${country}, ${formatPlaces(i18n, count)}`}
      className="focus-blue flex w-full flex-col gap-2 rounded text-left disabled:opacity-60"
    >
      <span className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-md bg-container-neutral-minimal">
        {isLoadingPhoto ? (
          <CircleNotchIcon
            size={20}
            aria-hidden="true"
            className="animate-spin text-fg-neutral-subtle"
          />
        ) : photoUrl ? (
          <img
            src={photoUrl}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <VenuePlaceholderImage className="h-full w-full" />
        )}

        {isSelecting ? (
          <span className="absolute inset-0 flex items-center justify-center bg-surface-primary/70">
            <CircleNotchIcon
              size={20}
              aria-hidden="true"
              className="animate-spin text-fg-brand-strong"
            />
          </span>
        ) : null}
      </span>

      <span aria-hidden="true" className="flex flex-col">
        <span className="label-s-regular text-fg-neutral-subtle">
          {country}
        </span>
        <span className="body-l-semibold break-words text-fg-neutral-strong">
          {name}
        </span>
        <span className="label-s-regular text-fg-neutral-subtle">
          {formatPlaces(i18n, count)}
        </span>
      </span>
    </button>
  );
};

export default OnboardingCityCard;
